import { useState, Fragment } from "react";
import type { DashboardUser } from "./api";
import { Sparkline } from "./Sparkline";
import { UserHealthCard } from "./UserHealthCard";
import { env } from "@/config/env";

interface UserTableProps {
  users: DashboardUser[];
  onDisable: (email: string) => void;
  onEnable: (email: string) => void;
  adminEmail: string;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "Never";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffH = Math.floor((now - then) / 3600000);
  if (diffH < 1) return "Just now";
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Yesterday";
  if (diffD < 7) return `${diffD}d ago`;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function TierBadge({ tier }: { tier: string }) {
  const lower = tier.toLowerCase();
  let cls = "cx-admin-badge cx-admin-badge--free";
  if (lower.startsWith("max")) cls = "cx-admin-badge cx-admin-badge--max";
  else if (lower === "pro") cls = "cx-admin-badge cx-admin-badge--pro";
  return <span className={cls}>{tier}</span>;
}

export function UserTable({ users, onDisable, onEnable, adminEmail }: UserTableProps) {
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<string | null>(null);

  return (
    <table className="cx-admin-table">
      <thead>
        <tr>
          <th>User</th>
          <th>Tier</th>
          <th>Last active</th>
          <th>7 days</th>
          <th style={{ textAlign: "right" }}>Messages</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const isProblem =
            u.has_friction || u.health === "declining" || u.health === "churning";
          const isInvited = u.status === "invited";
          const isExpanded = expandedUserId === u.user_id;
          const isStale =
            !u.last_active ||
            Date.now() - new Date(u.last_active).getTime() > 5 * 24 * 3600000;

          return (
            <Fragment key={u.email}>
              <tr
                className={`cx-admin-user-row ${isProblem ? "cx-admin-row--problem" : ""}`}
                style={isInvited ? { opacity: 0.5 } : undefined}
                onClick={() => {
                  if (isInvited) return;
                  setExpandedUserId(isExpanded ? null : u.user_id);
                }}
              >
                <td>
                  <span
                    className={isProblem ? "cx-admin-email--problem" : ""}
                    style={{ fontWeight: 500 }}
                  >
                    {u.email}
                  </span>
                </td>
                <td>
                  <TierBadge tier={u.tier} />
                </td>
                <td>
                  <span
                    className={`cx-admin-last-active ${isStale ? "cx-admin-last-active--stale" : ""}`}
                  >
                    {isInvited ? "Invited" : formatRelative(u.last_active)}
                  </span>
                </td>
                <td>
                  {u.sparkline && u.sparkline.length > 0 && (
                    <Sparkline
                      data={u.sparkline}
                      color={isProblem ? "var(--color-property-warm)" : undefined}
                    />
                  )}
                </td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>{u.messages_7d}</td>
                <td style={{ textAlign: "right", width: 80 }}>
                  {!isInvited && (
                    <span
                      style={{
                        color: "var(--color-text-placeholder)",
                        fontSize: "var(--cx-text-xs)",
                      }}
                    >
                      {isExpanded ? "\u25BC" : "\u25B6"}
                    </span>
                  )}
                  {u.status === "disabled" && (
                    <button
                      className="cx-admin-btn"
                      style={{ fontSize: "var(--cx-text-xs)" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnable(u.email);
                      }}
                    >
                      Enable
                    </button>
                  )}
                  {u.status !== "disabled" &&
                    u.email.toLowerCase() !== adminEmail.toLowerCase() &&
                    !env.admin.emails.includes(u.email.toLowerCase()) &&
                    (confirmDisable === u.email ? (
                      <span
                        className="cx-admin-confirm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="cx-admin-btn cx-admin-btn--danger"
                          style={{ fontSize: "var(--cx-text-xs)" }}
                          onClick={() => {
                            onDisable(u.email);
                            setConfirmDisable(null);
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          className="cx-admin-btn"
                          style={{ fontSize: "var(--cx-text-xs)" }}
                          onClick={() => setConfirmDisable(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        className="cx-admin-btn cx-admin-btn--danger"
                        style={{ fontSize: "var(--cx-text-xs)" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDisable(u.email);
                        }}
                      >
                        Disable
                      </button>
                    ))}
                </td>
              </tr>
              {isExpanded && (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <UserHealthCard
                      userId={u.user_id}
                      email={u.email}
                      tier={u.tier}
                      created={u.created}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
